import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';

function SEO({
  title = '',
  description = '',
  lang = '',
  meta = '',
  og_title = '',
  og_image = '',
  og_description = '',
  twitter_title = '',
  twitter_description = '',
  twitter_image = '',
  pageContext={}
}) {

  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            siteUrl
            description
            author
          }
        }
      }
    `
  );

  const host = site?.siteMetadata?.siteUrl;
  const metaDescription = description || site.siteMetadata.description;
  const ogTitle = og_title || title;
  const ogDescription = og_description || metaDescription;
  const ogImage = og_image || 'https://boom.co/images/og.png';
  const twitterTitle = twitter_title || title;
  const twitterDescription = twitter_description || metaDescription;
  const twitterImage = twitter_image || og_image;

  let links = [];

  if (pageContext?.slug?.length > 0 ) {
    const normalisedPath = (pathStr='') => pathStr.replace(/([^:]\/)\/+/g, "$1");
    const normalisedPathWithHost = (pathStr='') => normalisedPath(host + '/' + pathStr + '/')
    const slug = pageContext.slug;
    const fullSlug = normalisedPath(pageContext.full_slug);
    const translatedSlugs = pageContext.translated_slugs;
    const defaultFullSlug = normalisedPath(pageContext.default_full_slug);


    let canonical = slug === 'home' ? host : normalisedPathWithHost(fullSlug);
    /* use default url for resource items as they aren't actually translated */
    if (/^(Post|download)$/.test(pageContext?.field_component)) {
      canonical = normalisedPathWithHost(defaultFullSlug);
    }


    links = [
      {
        rel: 'canonical',
        href: canonical,
      },
      {
        rel: 'alternate',
        hrefLang: 'x-default',
        href: slug === 'home' ? host : normalisedPathWithHost(defaultFullSlug)
      },
      {
        rel: 'alternate',
        hrefLang: 'en',
        href: slug === 'home' ? host : normalisedPathWithHost(defaultFullSlug)
      },
      ...translatedSlugs?.map(({ lang, path }) => {
        return {
          rel: 'alternate',
          hrefLang: lang,
          href: normalisedPathWithHost(`/${lang}/${slug !== 'home' ? path+'/' : ''}`),
        }
      }),
    ];
  }

  return (

    <Helmet
      htmlAttributes={{
        lang: lang === 'default' ? 'en': lang,
      }}
      title={title}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: ogTitle,
        },
        {
          property: `og:description`,
          content: ogDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          name: `og:image`,
          content: ogImage,
        },
        {
          property: `og:locale`,
          content: lang,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata?.author || ``,
        },
        {
          name: `twitter:title`,
          content: twitterTitle,
        },
        {
          name: `twitter:image`,
          content: twitterImage,
        },
        {
          name: `twitter:description`,
          content: twitterDescription,
        },
      ].concat(meta)}
      link={links}
    />
  );
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
};

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
};

export default SEO;
